import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Reveal, MaskLine } from "@/components/Reveal";
import { sendInquiry } from "@/api";
import Seo from "@/components/Seo";

export default function Contact() {
  const { cart, t, lang } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [include, setInclude] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | sending | done

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus("sending");
    try {
      await sendInquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        items: include ? cart.map((p) => ({ id: p.id, name: p.name, brand: p.brand, price_rub: p.price_rub, quantity: p.quantity || 1, size: p.size || null })) : [],
      });
      setStatus("done");
      toast.success(t.inquiry.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("idle");
      toast.error(t.inquiry.error);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24 px-5 md:px-10" data-testid="contact-page">
      <Seo title={t.seo.contact.title} description={t.seo.contact.description} />
      <header className="mb-12 md:mb-16">
        <Reveal><p className="text-xs uppercase tracking-[0.28em] text-muted mb-6">{t.hero.eyebrow}</p></Reveal>
        <h1 className="font-display font-light text-[20vw] lg:text-[12rem] leading-[0.8] tracking-tighter">
          <MaskLine delay={0.1}>{t.inquiry.open}</MaskLine>
        </h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: info */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-display text-3xl md:text-4xl leading-snug mb-8">{t.inquiry.subtitle}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="space-y-5 border-t border-line pt-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">{t.footer.whatsapp} ({lang === "ru" ? "ЕВР" : "EU"})</p>
                <a href="https://wa.me/79263070808" target="_blank" rel="noopener noreferrer" className="link-underline text-lg" data-testid="contact-whatsapp-eu">+7 926 307 08 08</a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">{t.footer.whatsapp} ({lang === "ru" ? "США" : "US"})</p>
                <a href="https://wa.me/17542712282" target="_blank" rel="noopener noreferrer" className="link-underline text-lg" data-testid="contact-whatsapp-us">+1 754-271-2282</a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">Telegram</p>
                <a href="https://t.me/s/gelogaevaheda" target="_blank" rel="noopener noreferrer" className="link-underline text-lg block" data-testid="contact-telegram-1">@gelogaevaheda</a>
                <a href="https://t.me/s/hedageogaeva" target="_blank" rel="noopener noreferrer" className="link-underline text-lg block mt-1" data-testid="contact-telegram-2">@hedageogaeva</a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">Instagram</p>
                <a href="https://www.instagram.com/gelogaeva_gold/" target="_blank" rel="noopener noreferrer" className="link-underline text-lg" data-testid="contact-instagram">@gelogaeva_gold</a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">Email</p>
                <a href="mailto:support@buyheda.com" className="link-underline text-lg" data-testid="contact-email">support@buyheda.com</a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7 lg:pl-10 lg:border-l border-line">
          {status === "done" ? (
            <div className="py-16 text-center flex flex-col items-center" data-testid="inquiry-success">
              <div className="h-14 w-14 rounded-full bg-ink text-paper flex items-center justify-center mb-6">
                <Check className="h-6 w-6" />
              </div>
              <p className="font-display text-3xl mb-3">{t.inquiry.success}</p>
              <button onClick={() => setStatus("idle")} className="mt-4 text-xs uppercase tracking-[0.2em] link-underline" data-testid="inquiry-reset">
                {t.inquiry.send}
              </button>
            </div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              onSubmit={submit} className="space-y-7 max-w-xl" data-testid="inquiry-form"
            >
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.inquiry.name}</label>
                <input required value={form.name} onChange={update("name")} data-testid="inquiry-name"
                  className="mt-2 w-full bg-transparent border-b border-line py-2 text-lg font-light focus:outline-none focus:border-ink transition-colors" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.inquiry.email}</label>
                <input required type="email" value={form.email} onChange={update("email")} data-testid="inquiry-email"
                  className="mt-2 w-full bg-transparent border-b border-line py-2 text-lg font-light focus:outline-none focus:border-ink transition-colors" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.inquiry.phone}</label>
                <input value={form.phone} onChange={update("phone")} data-testid="inquiry-phone"
                  className="mt-2 w-full bg-transparent border-b border-line py-2 text-lg font-light focus:outline-none focus:border-ink transition-colors" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.inquiry.message}</label>
                <textarea rows={4} value={form.message} onChange={update("message")} placeholder={t.inquiry.messagePlaceholder} data-testid="inquiry-message"
                  className="mt-2 w-full bg-transparent border-b border-line py-2 text-base font-light focus:outline-none focus:border-ink transition-colors resize-none placeholder:text-ink/30" />
              </div>

              {cart.length > 0 && (
                <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="inquiry-include">
                  <span className={`h-5 w-5 border border-ink flex items-center justify-center transition-colors ${include ? "bg-ink text-paper" : "bg-transparent"}`}>
                    {include && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} className="sr-only" />
                  <span className="text-sm">{t.inquiry.include} ({cart.length})</span>
                </label>
              )}

              <button type="submit" disabled={status === "sending"} data-testid="inquiry-submit"
                className="group flex items-center justify-center gap-3 w-full bg-ink text-paper py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink disabled:opacity-60">
                {status === "sending" ? t.inquiry.sending : t.inquiry.send}
                {status !== "sending" && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </div>
  );
}
